/**
 * FolderList — barra de pastas `.folderbar` EXATA do mockup automation-workflows:
 * UMA linha de chips `.fchip2` (30px pill): "Todos" (home) + pastas com dot 9px
 * colorido + count, ativa = lilás sem borda; "＋ Nova pasta" dashed no fim.
 * Funcionalidade preservada: seleção, navegação por breadcrumb (ancestrais viram
 * chips), drag-and-drop de workflow para chip/Todos, editar/excluir (⋮ no chip
 * ativo — realocação: o mockup não define slot de gestão da pasta).
 */

import React, { useState } from 'react';
import type { WorkflowFolderResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';

interface FolderListProps {
  folders: WorkflowFolderResponse[];
  currentFolder?: WorkflowFolderResponse | null;
  breadcrumb?: WorkflowFolderResponse[];
  isLoading?: boolean;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: () => void;
  onEditFolder: (folder: WorkflowFolderResponse) => void;
  onDeleteFolder: (folderId: string) => void;
  /** Handler for dropping workflow into a folder */
  onDropWorkflow?: (workflowId: string, folderId: string) => void;
  /** Handler for dropping workflow to root (remove from folder) */
  onDropWorkflowToRoot?: (workflowId: string) => void;
}

export const FolderList: React.FC<FolderListProps> = ({
  folders,
  currentFolder,
  breadcrumb = [],
  isLoading,
  onSelectFolder,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onDropWorkflow,
  onDropWorkflowToRoot,
}) => {
  const [dragOverId, setDragOverId] = useState<string | 'root' | null>(null);

  const handleDragOver = (e: React.DragEvent, id: string | 'root') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  };

  const handleDrop = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    setDragOverId(null);
    const workflowId = e.dataTransfer.getData('workflow-id');
    if (workflowId) onDropWorkflow?.(workflowId, folderId);
  };

  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
    const workflowId = e.dataTransfer.getData('workflow-id');
    if (workflowId) onDropWorkflowToRoot?.(workflowId);
  };

  /** Chip `.fchip2` de pasta (dot + nome + count) — usado p/ ancestrais e nível atual */
  const FolderChip: React.FC<{ folder: WorkflowFolderResponse }> = ({ folder }) => {
    const on = currentFolder?.id === folder.id;
    const over = dragOverId === folder.id;

    // Gestão da pasta — dropdown padrão do DS (F78: CardActionsMenu, fim do menu ad-hoc)
    const folderActions: CardAction[] = [
      { label: 'Renomear', icon: 'edit', onClick: () => onEditFolder(folder) },
      { label: 'Excluir', icon: 'trash', onClick: () => onDeleteFolder(folder.id), variant: 'danger' },
    ];

    return (
      <div
        className="relative flex-none inline-flex items-center gap-[7px] rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors"
        style={{
          height: 30,
          paddingLeft: 12,
          paddingRight: on ? 6 : 12,
          border: on || over ? '1px solid transparent' : '1px solid var(--theme-card-border)',
          backgroundColor: on || over ? 'var(--theme-primary-background)' : 'var(--theme-card-background)',
          color: on || over ? 'var(--theme-primary)' : 'var(--theme-card-text)',
          outline: over ? '2px solid var(--theme-primary)' : 'none',
        }}
        onDragOver={(e) => handleDragOver(e, folder.id)}
        onDragLeave={() => setDragOverId(null)}
        onDrop={(e) => handleDrop(e, folder.id)}
      >
        <button
          onClick={() => onSelectFolder(folder.id)}
          className="inline-flex items-center gap-[7px] h-full"
          style={{ color: 'inherit' }}
          data-testid={`folder-chip-${folder.id}`}
        >
          <span className="flex-none" style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: folder.color || '#8b5cf6' }} />
          {folder.name}
          {typeof folder.workflowCount === 'number' && (
            <span className="text-[11px]" style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
              {folder.workflowCount}
            </span>
          )}
        </button>
        {/* Gestão da pasta (⋮) — só no chip ATIVO (anatomia do mockup) */}
        {on && (
          <CardActionsMenu actions={folderActions} size="sm" data-testid={`folder-menu-${folder.id}`} />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 overflow-x-auto" style={{ padding: '10px 16px' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-[30px] w-24 rounded-full animate-pulse flex-none" style={{ backgroundColor: 'var(--theme-border)' }} />
        ))}
      </div>
    );
  }

  // Chips do nível atual: ancestrais (breadcrumb) primeiro, depois as pastas visíveis
  const ancestorIds = new Set(breadcrumb.map((f) => f.id));
  const levelFolders = folders.filter((f) => !ancestorIds.has(f.id));

  return (
    /* `.folderbar` do mockup: barra full-width, bg surface, border-b, scroll-x */
    <div
      className="flex-none flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{
        padding: '10px 16px',
        backgroundColor: 'var(--theme-header-background)',
        borderBottom: '1px solid var(--theme-header-border)',
      }}
      data-testid="workflows-folderbar"
    >
      {/* "Todos" (home) */}
      <button
        onClick={() => onSelectFolder(null)}
        className="inline-flex items-center gap-[7px] rounded-full text-[13px] font-semibold whitespace-nowrap flex-none transition-colors"
        style={{
          height: 30,
          padding: '0 12px',
          border: !currentFolder || dragOverId === 'root' ? '1px solid transparent' : '1px solid var(--theme-card-border)',
          backgroundColor: !currentFolder || dragOverId === 'root' ? 'var(--theme-primary-background)' : 'var(--theme-card-background)',
          color: !currentFolder || dragOverId === 'root' ? 'var(--theme-primary)' : 'var(--theme-card-text)',
          outline: dragOverId === 'root' ? '2px solid var(--theme-primary)' : 'none',
        }}
        onDragOver={(e) => handleDragOver(e, 'root')}
        onDragLeave={() => setDragOverId(null)}
        onDrop={handleRootDrop}
        data-testid="folder-chip-all"
      >
        <Icon name="home" size={14} />
        Todos
      </button>

      {breadcrumb.map((folder) => (
        <FolderChip key={folder.id} folder={folder} />
      ))}
      {levelFolders.map((folder) => (
        <FolderChip key={folder.id} folder={folder} />
      ))}

      {/* `.fnew`: Nova pasta dashed */}
      <button
        onClick={onCreateFolder}
        className="inline-flex items-center gap-[7px] rounded-full text-[13px] font-semibold whitespace-nowrap flex-none transition-colors"
        style={{
          height: 30,
          padding: '0 12px',
          border: '1px dashed var(--theme-input-border)',
          backgroundColor: 'transparent',
          color: 'var(--theme-muted-text)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--theme-primary)'; e.currentTarget.style.color = 'var(--theme-primary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--theme-input-border)'; e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
        data-testid="new-folder"
      >
        <Icon name="plus" size={14} />
        Nova pasta
      </button>
    </div>
  );
};

export default FolderList;
